import { Portal } from 'components/Portal';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { transparentize } from 'polished';
import React, { useRef } from 'react';
import styled, { css } from 'styled-components/macro';

import { isMobile } from '../../utils/userAgent';

const ModalWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  z-index: 5;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div<{ minHeight?: number; maxHeight?: number; mobile: boolean }>`
  background-color: ${({ theme }) => theme.modalBG};
  backdrop-filter: blur(50.7px);
  box-shadow: ${({ theme }) => `0 4px 8px 0 ${transparentize(0.95, theme.shadow1)}`};
  padding: 0px;
  width: 50vw;
  overflow-y: auto;
  overflow-x: hidden;

  align-self: ${({ mobile }) => (mobile ? 'flex-end' : 'center')};

  max-width: 420px;
  ${({ maxHeight }) =>
    maxHeight &&
    css`
      max-height: ${maxHeight}vh;
    `}
  ${({ minHeight }) =>
    minHeight &&
    css`
      min-height: ${minHeight}vh;
    `}
  display: flex;
  border-radius: 20px;
  ${({ theme }) => theme.mediaWidth.upToMedium`
  width: 65vw;
  margin: 0;
`}
  ${({ theme, mobile }) => theme.mediaWidth.upToSmall`
  width:  85vw;
  ${
    mobile &&
    css`
      width: 100vw;
      border-radius: 20px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `
  }
`}
`;

interface ModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  minHeight?: number;
  maxHeight?: number;
  initialFocusRef?: React.RefObject<any>;
  children?: React.ReactNode;
}

export default function Modal({
  isOpen,
  onDismiss,
  minHeight = undefined,
  maxHeight = 90,
  initialFocusRef,
  children,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, isOpen ? onDismiss : undefined, [modalRef]);

  if (!isOpen) return <></>;

  return (
    <Portal>
      <span ref={modalRef}>
        <ModalWrapper>
          <ModalContent minHeight={minHeight} maxHeight={maxHeight} mobile={isMobile}>
            {/* prevents the automatic focusing of inputs on mobile by the reach dialog */}
            {!initialFocusRef && isMobile ? <div tabIndex={1} /> : null}
            {children}
          </ModalContent>
        </ModalWrapper>
      </span>
    </Portal>
  );
}
